/**
 * Description bottom info component.
 */
import Container from '../../atoms/container/descriptionBottomInfoContainer';
import Score from '../rating/score';
import Separator from '../../atoms/container/descriptionBarSeparator';
import Tip from '../tip/tip';
import { useMemo } from 'react';

/**
 * Props for the DescriptionBottomInfo component.
 *
 * @param {boolean} displaySeparator - Whether to display the separator
 * @param {boolean} displayTip - Whether to display the tip
 * @param {string} score - Score of the content
 */
interface Props {
  displaySeparator?: boolean;
  displayTip?: boolean;
  score?: string;
}

/**
 * Renders the bottom information section of content descriptions.
 * Shows score, separator, and tip information.
 *
 * @returns {JSX.Element} Description bottom info component
 */
export default function DescriptionBottomInfo(parentProps: Props) {
  // Hook
  const { display, props } = useBottomInfo(parentProps);

  // Render
  return (
    <Container id='descriptionBottomInfo'>
      {display.score ? <Score {...props.score} /> : null}
      {display.separator ? <Separator /> : null}
      {display.tip ? <Tip /> : null}
    </Container>
  );
}

// Hook to get the bottom information of the currently focused asset in a rail.
function useBottomInfo(parentProps: Props) {
  // Memoize the bottom information
  return useMemo(() => {
    const { score, displaySeparator, displayTip } = parentProps;
    return {
      display: {
        score: !!score,
        separator: !!displaySeparator,
        tip: !!displayTip,
      },
      props: {
        score: {
          score,
        },
      },
    };
  }, [parentProps]);
}
